// @ts-nocheck
/* eslint-disable react/prop-types */
/**
 * GroupNode — Renders an empty box used to visually group other nodes.
 *
 * Supports:
 *   data.label       — display text (rendered at the top of the group)
 *   data.colorScheme — fallback theme preset
 *   data.color       — explicit BG color
 *   data.borderColor — explicit border color
 *   data.menuItems   — right-click options for the group itself
 */
import React, { memo, useMemo } from 'react';
import { NodeResizeControl } from 'reactflow';
import { useTheme } from '@mui/material/styles';
import SolyticsBox from '../../../SolyticsBox';
import SolyticsTypography from '../../../SolyticsTypography';
import NodeMenu from '../shared/NodeMenu';
import { resolveThemeColor } from '../shared/themeColorUtils';
import { ReactComponent as ResizeIcon } from '../../../../assets/GroupResize.svg';

export const GroupShape = memo(({ label, colors, children }) => {
  return (
    <SolyticsBox
      width="100%"
      height="100%"
      minWidth={120}
      minHeight={80}
      borderRadius={1}
      bgcolor={colors.bg}
      p={1}
      position="relative"
      border={`1.5px dashed ${colors.border}`}
    >
      <SolyticsTypography color={colors.text} noWrap gutterBottom>
        {label}
      </SolyticsTypography>
      {children}
    </SolyticsBox>
  );
});
GroupShape.displayName = 'GroupShape';

const GroupNode = memo(({ id, data, selected, parentId }) => {
  const theme = useTheme();

  const colors = useMemo(
    () => ({
      bg: resolveThemeColor(
        theme,
        data?.bgColor ?? data?.color,
        'solyticsFlow.node.groupBg'
      ),
      border: resolveThemeColor(
        theme,
        data?.borderColor,
        'solyticsFlow.node.groupBorder'
      ),
      text: resolveThemeColor(
        theme,
        data?.textColor,
        'solyticsFlow.node.groupText'
      ),
    }),
    [data, theme]
  );

  return (
    <NodeMenu menuItems={data?.menuItems} nodeId={id} parentId={parentId}>
      <GroupShape label={data?.label} colors={colors}>
        <NodeResizeControl
          minWidth={120}
          minHeight={80}
          isVisible={selected && !data?.isReadOnly}
          style={{ background: 'transparent', border: 'none' }}
        >
          <SolyticsBox
            position="absolute"
            right={2}
            bottom={2}
            width={14}
            height={14}
          >
            <ResizeIcon color={colors.border} />
          </SolyticsBox>
        </NodeResizeControl>
      </GroupShape>
    </NodeMenu>
  );
});

GroupNode.displayName = 'GroupNode';

export default GroupNode;
